/**
 * Proposing the current branch for review.
 *
 * Every field here is optional, and every empty one means "work it out": the
 * title becomes the issue's own title, and the description is prepended to the
 * repository's merge request template. Most people will type nothing at all,
 * so the dialog says what BEST will write rather than asking them to write it.
 *
 * `Closes #42` is the part that matters and the part nobody should have to
 * remember, so it is stated here before anything is created - and when BEST
 * does not know which issue the branch is for, that is asked rather than
 * quietly dropped, because a merge request that closes nothing looks identical
 * to one that closes something.
 *
 * Reviewers (issue #67) are optional and are shown, never required. BEST
 * encodes no review policy: people GitLab says can merge are marked in the
 * picker, and when none of the people chosen is marked the dialog says so in one
 * quiet line - "none can merge" only when GitLab said so for EVERY one of them,
 * and "could not tell" when any answer was null (not known is not "no") - and
 * still lets the proposal go, because GitLab enforces who may merge and somebody
 * else can. Nothing on this form is disabled for who was or was not chosen. The merge request is assigned to whoever proposes it; that
 * is a convenience, stated here rather than asked.
 *
 * A NEW merge request is also given the owners GitLab names for the files it
 * changes (issue #67, phase C) - the dialog says so in one sentence and offers
 * no switch for it. BEST asks GitLab who they are; it does not read CODEOWNERS.
 *
 * Reviewers are SENT only when they would change something. A merge request
 * that is already open has reviewers of its own; the picker shows them, and the
 * key is left out of the request unless the selection was edited, so merely
 * opening this dialog can never rewrite who was asked.
 *
 * The same dialog is also how reviewers are changed on a merge request that is
 * ALREADY open (`mode="reviewers"`): one component with a mode, not a second
 * dialog, because the picker, the people list and the "can they merge" line are
 * the same thing in both. In that mode only the picker is shown, the request
 * carries nothing but `reviewer_ids` (the server's idempotent path sets them on
 * the open merge request and returns it), and Save waits for a real difference -
 * saving an unchanged list would be a request that does nothing.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { MergeRequest, PersonMergeHint } from '../../schema'
import { MONOSPACE } from '../../theme'
import { useMembers } from './members'
import PeoplePicker from './PeoplePicker'

/** The same people, whatever order they were picked in. */
function sameIds(left: PersonMergeHint[], right: PersonMergeHint[]): boolean {
  const ids = new Set(left.map((person) => person.id))
  return ids.size === right.length && right.every((person) => ids.has(person.id))
}

export default function ProposeMergeDialog({
  open,
  slug,
  branch,
  target,
  issue,
  existing = null,
  mode = 'propose',
  onClose,
  onProposed,
}: {
  open: boolean
  slug: string
  branch: string
  /** The branch it will merge into, so the dialog can state it. */
  target: string
  /** The issue BEST remembers for this branch, or '' when it knows of none. */
  issue: string
  /**
   * The merge request already open for this branch, when the screen knows of
   * one. The dialog cannot find out for itself: it makes no request about the
   * merge request, only about the people.
   */
  existing?: MergeRequest | null
  /**
   * 'propose' opens (or finds) a merge request; 'reviewers' only changes the
   * reviewers of `existing`, and is meaningless without one.
   */
  mode?: 'propose' | 'reviewers'
  onClose: () => void
  /** The merge request GitLab answered with - in either mode. */
  onProposed: (proposal: MergeRequest) => void
}) {
  const editing = mode === 'reviewers' && existing !== null
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  // undefined = not edited, so an open merge request's own reviewers show and
  // are left alone. Any array - even an emptied one - is a decision.
  const [picked, setPicked] = useState<PersonMergeHint[] | undefined>(undefined)
  const members = useMembers(slug, open)
  const current = existing?.reviewers ?? []
  const reviewers = picked ?? current
  // Compared as a set: choosing somebody and then removing them again is not
  // a change worth rewriting an open merge request's reviewers for.
  const edited = picked !== undefined && !sameIds(current, picked)
  const rule = members.status === 'ready' ? members.data.merge_rule : null
  const [issueText, setIssueText] = useState('')
  const [busy, setBusy] = useState(false)
  // Separated from `error` because the two read differently: 'nothing to
  // merge' is an ordinary answer to a reasonable question, and colouring it
  // red would say the tool went wrong when the branch simply has no commits.
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const typedIssue = issueText.trim()
  const badIssue = typedIssue !== '' && !/^\d+$/.test(typedIssue)
  const closes = issue || typedIssue

  const propose = async () => {
    setBusy(true)
    setNotice('')
    setError('')
    try {
      const proposal = await api.proposeMerge(
        slug,
        editing
          ? // Nothing else: a title or description here would be read as a
            // wish to change the open merge request's text.
            { reviewer_ids: reviewers.map((person) => person.id) }
          : {
              ...(title.trim() ? { title: title.trim() } : {}),
              ...(description.trim() ? { description: description.trim() } : {}),
              // Only when BEST does not already know: passing the wrong issue
              // is worse than passing none, because 'Closes #42' would close
              // somebody else's work.
              ...(!issue && typedIssue ? { issue: Number(typedIssue) } : {}),
              // An open merge request keeps its reviewers unless they were
              // edited (an emptied list is sent, as []: that is how somebody
              // is taken off). With none open there is nothing to keep, so
              // whoever was chosen is sent, and nothing at all when nobody was.
              ...((existing ? edited : reviewers.length > 0)
                ? { reviewer_ids: reviewers.map((person) => person.id) }
                : {}),
            },
      )
      setTitle('')
      setDescription('')
      setIssueText('')
      setPicked(undefined)
      onProposed(proposal)
    } catch (failure) {
      const refused = failure as ApiError
      // 409 is a state, not a fault: the branch is at the same commit as the
      // target, so there is nothing in it to review yet.
      if (refused.status === 409) setNotice(refused.message)
      else setError(refused.message || 'Could not propose this branch.')
    } finally {
      setBusy(false)
    }
  }

  // Leaving without saving drops the edit: the next time this opens it must
  // show who is on the merge request now, not what was abandoned.
  const close = () => {
    if (editing) setPicked(undefined)
    onClose()
  }

  // Information, never a gate. Said only once the rule is known, and only when
  // somebody is chosen. "Cannot" is said only when GitLab said so for EVERY
  // person chosen: a null is GitLab not knowing, and that is not a "no".
  const chosenCanMerge = reviewers.some((person) => person.can_merge === true)
  const mergeNote =
    !rule || reviewers.length === 0 || chosenCanMerge
      ? null
      : reviewers.every((person) => person.can_merge === false)
        ? `None of these people can merge into ${rule.branch} (${rule.description} only), so somebody else will have to merge it.`
        : `BEST could not tell from GitLab whether any of these people can merge into ${rule.branch}.`

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Change reviewers' : 'Propose for review'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {editing ? (
            <>
              Who is asked to review !{existing?.iid}. The people already on it
              are shown; nothing else about the merge request changes.
            </>
          ) : (
            <>
              BEST opens a merge request from{' '}
              <Box component="span" sx={{ fontFamily: MONOSPACE }}>
                {branch}
              </Box>{' '}
              into{' '}
              <Box component="span" sx={{ fontFamily: MONOSPACE }}>
                {target}
              </Box>
              . It does not merge it. Merging is a separate step, and GitLab's
              approvals and pipeline decide whether it can.
            </>
          )}
        </DialogContentText>

        <Stack spacing={2}>
          {editing ? null : (
            <>
              <TextField
                label="Title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder={issue ? `The title of issue #${issue}` : 'Model changes on this branch'}
                helperText="Optional. Left empty, the issue's own title is used."
                fullWidth
              />
              <TextField
                label="Anything the reviewer should know"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                multiline
                minRows={3}
                helperText="Optional. Goes above the repository's merge request template."
                fullWidth
              />
            </>
          )}

          <PeoplePicker
            multiple
            label="Reviewers"
            members={members}
            value={reviewers}
            onChange={setPicked}
            helperText="Optional. People GitLab says can merge are marked."
          />
          {/* The submit button below does not look at this. */}
          {mergeNote ? (
            <Typography variant="body2" color="text.secondary">
              {mergeNote}
            </Typography>
          ) : null}

          {/* Said only when a NEW merge request is about to be opened: the
              server adds the owners GitLab names then and never to one already
              open. BEST asks GitLab who they are; it does not read CODEOWNERS. */}
          {editing || existing ? null : (
            <Typography variant="body2" color="text.secondary">
              The owners of the files this changes are asked to review automatically.
            </Typography>
          )}

          {existing ? null : (
            <Typography variant="body2" color="text.secondary">
              It will be assigned to you.
            </Typography>
          )}

          {editing ? null : issue ? (
            <Typography variant="body2" color="text.secondary">
              The description opens with{' '}
              <Box component="span" sx={{ fontFamily: MONOSPACE }}>
                Closes #{issue}
              </Box>
              , so merging this closes the issue the work was raised against.
            </Typography>
          ) : (
            <TextField
              label="Issue to close"
              value={issueText}
              onChange={(event) => setIssueText(event.target.value)}
              placeholder="42"
              error={badIssue}
              helperText={
                badIssue
                  ? 'A GitLab issue number, digits only.'
                  : 'Optional, and only needed because this branch was not started from BEST. Left empty, the merge request closes nothing.'
              }
              fullWidth
            />
          )}
        </Stack>

        {notice ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Nothing to propose yet</AlertTitle>
            <Box sx={{ whiteSpace: 'pre-line' }}>{notice}</Box>
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>{editing ? 'Could not save the reviewers' : 'Could not propose it'}</AlertTitle>
            <Box sx={{ whiteSpace: 'pre-line' }}>{error}</Box>
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Cancel
        </Button>
        {editing ? (
          // Waits for a real difference, and for nothing else: who was chosen
          // is not a condition, only whether there is anything to save.
          <Button
            onClick={() => void propose()}
            variant="contained"
            disabled={busy || !edited}
          >
            {busy ? 'Saving…' : 'Save reviewers'}
          </Button>
        ) : (
          <Button onClick={() => void propose()} variant="contained" disabled={busy || badIssue}>
            {busy ? 'Opening…' : closes ? `Propose, closing #${closes}` : 'Propose'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
